import { Plus, Trash2, Check, X as XIcon, Loader2, Pencil } from 'lucide-react';
import { useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';

import { supabase } from '../../../../lib/supabase';
import { useCurrentCompany } from '../../../../hooks/useCurrentCompany';

interface Location {
  id: string;
  name: string;
  timezone: string | null;
}

interface LocForm {
  name: string;
  timezone: string;
}

const EMPTY: LocForm = { name: '', timezone: '' };

function defaultTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

export function LocationsManager() {
  const { data: companyId } = useCurrentCompany();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<LocForm>({ name: '', timezone: defaultTimezone() });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<LocForm>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['company-locations', companyId],
    queryFn: async () => {
      if (!companyId) return [];
      const { data, error: qErr } = await supabase
        .from('locations')
        .select('id, name, timezone')
        .eq('company_id', companyId)
        .order('name');
      if (qErr) throw qErr;
      return (data || []) as Location[];
    },
    enabled: !!companyId,
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['company-locations', companyId] });

  const create = useMutation({
    mutationFn: async (f: LocForm) => {
      if (!companyId) throw new Error('No company selected');
      const { error: insErr } = await supabase
        .from('locations')
        .insert({ company_id: companyId, name: f.name.trim(), timezone: f.timezone.trim() || 'UTC' });
      if (insErr) throw insErr;
    },
    onSuccess: () => {
      setForm({ name: '', timezone: defaultTimezone() });
      invalidate();
    },
    onError: (e: Error) => setError(e.message),
  });

  const update = useMutation({
    mutationFn: async ({ id, f }: { id: string; f: LocForm }) => {
      const { error: updErr } = await supabase
        .from('locations')
        .update({ name: f.name.trim(), timezone: f.timezone.trim() || 'UTC' })
        .eq('id', id);
      if (updErr) throw updErr;
    },
    onSuccess: () => {
      setEditingId(null);
      invalidate();
    },
    onError: (e: Error) => setError(e.message),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error: delErr } = await supabase.from('locations').delete().eq('id', id);
      if (delErr) throw delErr;
    },
    onSuccess: invalidate,
    onError: (e: Error) => setError(e.message),
  });

  const handleAdd = () => {
    setError(null);
    if (!form.name.trim()) return;
    create.mutate(form);
  };

  const handleDelete = (id: string, name: string) => {
    setError(null);
    if (
      !window.confirm(
        `Delete "${name}"? This will fail if any active class, enrollment, or waiver references it.`
      )
    )
      return;
    remove.mutate(id);
  };

  return (
    <div className="max-w-2xl space-y-4">
      <div className="bg-[#1A1717] border border-[#504A4A] rounded-lg p-3 space-y-2">
        <p className="text-sm text-[#EBFFEE] font-medium">Add Location</p>
        <div className="flex gap-2">
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Name (e.g. Main Floor)"
            className="flex-1 bg-[#111010] border border-[#504A4A] rounded-lg px-3 py-2 text-sm text-[#EBFFEE] placeholder-[#504A4A] focus:outline-none focus:border-[#9B1421]"
          />
          <input
            type="text"
            value={form.timezone}
            onChange={(e) => setForm({ ...form, timezone: e.target.value })}
            placeholder="Timezone (e.g. America/Los_Angeles)"
            className="flex-1 bg-[#111010] border border-[#504A4A] rounded-lg px-3 py-2 text-sm text-[#EBFFEE] placeholder-[#504A4A] focus:outline-none focus:border-[#9B1421]"
          />
          <button
            onClick={handleAdd}
            disabled={create.isPending || !form.name.trim()}
            className="flex items-center gap-1 bg-[#9B1421] hover:bg-[#7A0F1A] text-white text-sm font-semibold px-3 py-2 rounded-lg disabled:opacity-50"
          >
            {create.isPending ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            Add
          </button>
        </div>
      </div>

      {error && (
        <div className="text-red-400 text-sm bg-red-900/20 border border-red-600/40 rounded-lg px-3 py-2">
          {error}
        </div>
      )}

      <div className="border border-[#363232] rounded-lg overflow-hidden">
        {query.isLoading ? (
          <div className="p-6 flex justify-center">
            <Loader2 size={20} className="animate-spin text-[#A9A3A3]" />
          </div>
        ) : query.data?.length === 0 ? (
          <p className="p-4 text-sm text-[#A9A3A3] text-center">No locations yet.</p>
        ) : (
          query.data?.map((l) => {
            const isEditing = editingId === l.id;
            return (
              <div
                key={l.id}
                className="flex items-center justify-between px-3 py-2 border-b border-[#363232] last:border-0 gap-2"
              >
                {isEditing ? (
                  <>
                    <input
                      type="text"
                      value={editForm.name}
                      onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                      className="flex-1 bg-[#111010] border border-[#504A4A] rounded px-2 py-1 text-sm text-[#EBFFEE] focus:outline-none focus:border-[#9B1421]"
                      autoFocus
                    />
                    <input
                      type="text"
                      value={editForm.timezone}
                      onChange={(e) => setEditForm({ ...editForm, timezone: e.target.value })}
                      className="flex-1 bg-[#111010] border border-[#504A4A] rounded px-2 py-1 text-sm text-[#EBFFEE] focus:outline-none focus:border-[#9B1421]"
                    />
                    <button
                      onClick={() => update.mutate({ id: l.id, f: editForm })}
                      disabled={update.isPending || !editForm.name.trim()}
                      className="p-1.5 text-green-400 hover:bg-green-900/20 rounded"
                    >
                      <Check size={14} />
                    </button>
                    <button
                      onClick={() => setEditingId(null)}
                      className="p-1.5 text-[#A9A3A3] hover:text-[#EBFFEE] hover:bg-[#363232] rounded"
                    >
                      <XIcon size={14} />
                    </button>
                  </>
                ) : (
                  <>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-[#EBFFEE]">{l.name}</p>
                      {l.timezone && <p className="text-xs text-[#A9A3A3]">{l.timezone}</p>}
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => {
                          setEditingId(l.id);
                          setEditForm({ name: l.name, timezone: l.timezone ?? '' });
                        }}
                        className="p-1.5 text-[#A9A3A3] hover:text-[#EBFFEE] hover:bg-[#363232] rounded"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={() => handleDelete(l.id, l.name)}
                        disabled={remove.isPending}
                        className="p-1.5 text-[#A9A3A3] hover:text-red-400 hover:bg-red-900/20 rounded disabled:opacity-50"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
